import axios from 'axios';
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({ baseURL: BASE_URL, timeout: 30000 });

// Request interceptor
api.interceptors.request.use(
  (config) => { const token = localStorage.getItem('accessToken'); if (token) config.headers.Authorization = `Bearer ${token}`; return config; },
  (err) => Promise.reject(err)
);

// Response interceptor with queue-based token refresh
let isRefreshing = false;
let failedQueue = [];
const processQueue = (error, token = null) => { failedQueue.forEach(p => error ? p.reject(error) : p.resolve(token)); failedQueue = []; };

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      if (isRefreshing) return new Promise((resolve, reject) => failedQueue.push({ resolve, reject })).then(token => { original.headers.Authorization = `Bearer ${token}`; return api(original); }).catch(e => Promise.reject(e));
      original._retry = true; isRefreshing = true;
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) throw new Error('No refresh token');
        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
        const newToken = data.data.accessToken;
        localStorage.setItem('accessToken', newToken);
        original.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return api(original);
      } catch {
        processQueue(new Error('Session expired'), null);
        localStorage.clear();
        toast.error('Your session has expired. Please log in again.', { duration: 3000 });
        setTimeout(() => { window.location.href = '/login'; }, 1500);
        return Promise.reject(err);
      } finally { isRefreshing = false; }
    }
    if (err.response?.status !== 401 && err.config?.responseType !== 'blob') {
      const message = err.response?.data?.error || err.message || 'Request failed';
      toast.error(message);
    }
    return Promise.reject(err);
  }
);

export default api;

export const authAPI = {
  login:   (data) => api.post('/auth/login', data),
  logout:  ()     => api.post('/auth/logout'),
  me:      ()     => api.get('/auth/me'),
};

export const dashboardAPI = {
  kpis:                () => api.get('/dashboard/kpis'),
  productionTrend:     () => api.get('/dashboard/production-trend'),
  assetHealth:         () => api.get('/dashboard/asset-health'),
  activity:            () => api.get('/dashboard/activity'),
  revenueVsTarget:     () => api.get('/dashboard/revenue-vs-target'),
  regulatoryCountdown: () => api.get('/dashboard/regulatory-countdown'),
};

export const assetsAPI = {
  list:        (params) => api.get('/assets', { params }),
  get:         (id)     => api.get(`/assets/${id}`),
  create:      (data)   => api.post('/assets', data),
  update:      (id, d)  => api.put(`/assets/${id}`, d),
  remove:      (id)     => api.delete(`/assets/${id}`),
  docs:        (id)     => api.get(`/assets/${id}/documents`),
  uploadDoc:   (id, fd) => api.post(`/assets/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  maintenance: (id)     => api.get(`/assets/${id}/maintenance`),
  aiInspection:(id, d)  => api.post(`/assets/${id}/ai-inspection`, d),
};

export const carbonAPI = {
  emissions:    (params) => api.get('/carbon/emissions', { params }),
  addEmission:  (data)   => api.post('/carbon/emissions', data),
  targets:      ()       => api.get('/carbon/targets'),
  setTarget:    (data)   => api.post('/carbon/targets', data),
  credits:      (params) => api.get('/carbon/credits', { params }),
  addCredit:    (data)   => api.post('/carbon/credits', data),
  taxonomy:     (params) => api.get('/carbon/taxonomy', { params }),
  generateCSRD: (year)   => api.post('/carbon/csrd/generate', { year }),
};

export const ppaAPI = {
  list:        (params) => api.get('/ppa', { params }),
  get:         (id)     => api.get(`/ppa/${id}`),
  create:      (data)   => api.post('/ppa', data),
  update:      (id, d)  => api.put(`/ppa/${id}`, d),
  remove:      (id)     => api.delete(`/ppa/${id}`),
  analyzeFile: (fd)     => api.post('/ppa/analyze', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const gridAPI = {
  logs:            (params) => api.get('/grid/logs', { params }),
  addLog:          (data)   => api.post('/grid/logs', data),
  importCSV:       (fd)     => api.post('/grid/logs/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  curtailment:     (params) => api.get('/grid/curtailment', { params }),
  addCurtailment:  (data)   => api.post('/grid/curtailment', data),
  feedInTariffs:   ()       => api.get('/grid/feed-in-tariffs'),
  offtake:         ()       => api.get('/grid/offtake'),
  generateSummary: (data)   => api.post('/grid/summary/generate', data),
};

export const regulatoryAPI = {
  list:      (params) => api.get('/regulatory', { params }),
  get:       (id)     => api.get(`/regulatory/${id}`),
  create:    (data)   => api.post('/regulatory', data),
  update:    (id, d)  => api.put(`/regulatory/${id}`, d),
  uploadDoc: (id, fd) => api.post(`/regulatory/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  prefill:   (id)     => api.post(`/regulatory/${id}/ai-prefill`),
};

export const financeAPI = {
  capex:          (params) => api.get('/finance/capex', { params }),
  addCapex:       (data)   => api.post('/finance/capex', data),
  opex:           (params) => api.get('/finance/opex', { params }),
  addOpex:        (data)   => api.post('/finance/opex', data),
  invoices:       (params) => api.get('/finance/invoices', { params }),
  addInvoice:     (data)   => api.post('/finance/invoices', data),
  updateInvoice:  (id, d)  => api.put(`/finance/invoices/${id}`, d),
  pnl:            (params) => api.get('/finance/pnl', { params }),
  anomalyCheck:   (data)   => api.post('/finance/anomaly-check', data),
  boardNarrative: (data)   => api.post('/finance/board-narrative', data),
};

export const maintenanceAPI = {
  alerts:       (params) => api.get('/maintenance/alerts', { params }),
  workOrders:   (params) => api.get('/maintenance/work-orders', { params }),
  addWorkOrder: (data)   => api.post('/maintenance/work-orders', data),
  updateWO:     (id, d)  => api.put(`/maintenance/work-orders/${id}`, d),
  perfLogs:     (params) => api.get('/maintenance/performance-logs', { params }),
  addPerfLog:   (data)   => api.post('/maintenance/performance-logs', data),
  rootCause:    (id)     => api.post(`/maintenance/alerts/${id}/root-cause`),
};

export const esgAPI = {
  metrics:            (params) => api.get('/esg/metrics', { params }),
  addMetric:          (data)   => api.post('/esg/metrics', data),
  griChecklist:       ()       => api.get('/esg/gri-checklist'),
  updateGriChecklist: (id, d)  => api.put(`/esg/gri-checklist/${id}`, d),
  reports:            ()       => api.get('/esg/reports'),
  generateReport:     (data)   => api.post('/esg/reports/generate', data),
};

export const auditAPI = {
  list:    (params) => api.get('/audit', { params }),
  actions: ()       => api.get('/audit/actions'),
};

export const aiAPI = {
  chat:            (data) => api.post('/ai/chat', data),
  conversations:   ()     => api.get('/ai/conversations'),
  conversation:    (id)   => api.get(`/ai/conversations/${id}`),
  newConversation: (data) => api.post('/ai/conversations', data),
  deleteConv:      (id)   => api.delete(`/ai/conversations/${id}`),
};

export const exportAPI = {
  assetsExcel:     ()     => api.get('/export/assets/excel',                  { responseType: 'blob' }),
  assetsPDF:       ()     => api.get('/export/assets/pdf',                    { responseType: 'blob' }),
  carbonExcel:     (year) => api.get('/export/carbon/excel',                  { params:{ year }, responseType: 'blob' }),
  csrdPDF:         (year) => api.get('/export/carbon/csrd/pdf',               { params:{ year }, responseType: 'blob' }),
  esgPDF:          (year) => api.get('/export/esg/pdf',                       { params:{ year }, responseType: 'blob' }),
  regulatoryExcel: ()     => api.get('/export/regulatory/excel',              { responseType: 'blob' }),
  regulatoryPDF:   ()     => api.get('/export/regulatory/pdf',                { responseType: 'blob' }),
  invoicesExcel:   ()     => api.get('/export/finance/invoices/excel',        { responseType: 'blob' }),
  workOrdersExcel: ()     => api.get('/export/maintenance/work-orders/excel', { responseType: 'blob' }),
  ppaExcel:        ()     => api.get('/export/ppa/excel',                     { responseType: 'blob' }),
  pnlPDF:          (year) => api.get('/export/finance/pnl/pdf',               { params:{ year }, responseType: 'blob' }),
  auditExcel:      ()     => api.get('/export/audit/excel',                   { responseType: 'blob' }),
};

export const settingsAPI = {
  all:        ()     => api.get('/settings'),
  update:     (data) => api.put('/settings', data),
  uploadLogo: (fd)   => api.post('/settings/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  health:     ()     => api.get('/settings/health'),
  backupInfo: ()     => api.get('/settings/backup-info'),
};

export const usersAPI = {
  list:       (params) => api.get('/users', { params }),
  get:        (id)     => api.get(`/users/${id}`),
  create:     (data)   => api.post('/users', data),
  update:     (id, d)  => api.put(`/users/${id}`, d),
  deactivate: (id)     => api.delete(`/users/${id}`),
};

export const seedAPI = {
  loadDemo:  () => api.post('/seed/demo'),
  clearDemo: () => api.delete('/seed/demo'),
};
